import { useState } from 'react';
import { Receipt } from 'lucide-react';
import { Button, type ButtonVariant } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { STAFF_RECEIPT_GRID_BUTTON_CLASS } from '@/lib/staffReceiptDownload';

/**
 * Order and tab detail share this action row, including the receipt download
 * special case. The parent still runs the mutation and owns the error string
 * so a sibling (Exact export) can write into the same message.
 */
export function WorkflowActionsCard<T extends string>({
  actions,
  label,
  variant,
  actionLoading,
  error,
  onError,
  onAction,
  onReceipt,
  receiptAction = 'DOWNLOAD_RECEIPT' as T,
}: {
  actions: T[];
  label: (action: T) => string;
  variant: (action: T) => ButtonVariant;
  actionLoading: boolean;
  error: string | null;
  onError: (message: string | null) => void;
  onAction: (action: T) => Promise<void>;
  onReceipt: () => Promise<void>;
  receiptAction?: T;
}) {
  const [receiptLoading, setReceiptLoading] = useState(false);
  if (actions.length === 0) return null;

  return (
    <Card title="Actions">
      <div className="flex flex-wrap items-center gap-2">
        {actions.map((action) =>
          action === receiptAction ? (
            <Button
              key={action}
              type="button"
              variant="outline"
              size="sm"
              className={STAFF_RECEIPT_GRID_BUTTON_CLASS}
              leftIcon={<Receipt className="h-4 w-4" aria-hidden />}
              loading={receiptLoading}
              disabled={receiptLoading || actionLoading}
              onClick={async () => {
                onError(null);
                setReceiptLoading(true);
                try {
                  await onReceipt();
                } catch (e) {
                  onError(e instanceof Error ? e.message : 'Receipt download failed.');
                } finally {
                  setReceiptLoading(false);
                }
              }}
            >
              {label(action)}
            </Button>
          ) : (
            <Button
              key={action}
              variant={variant(action)}
              size="sm"
              loading={actionLoading}
              disabled={actionLoading || receiptLoading}
              onClick={async () => {
                onError(null);
                try {
                  await onAction(action);
                } catch (e) {
                  onError(e instanceof Error ? e.message : 'Could not run this action.');
                }
              }}
            >
              {label(action)}
            </Button>
          ),
        )}
      </div>
      {error && (
        <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
    </Card>
  );
}
